import React, { useCallback, useContext } from "react";
import * as Sentry from "@sentry/react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { UI } from "@wwimmo/ui";
import styles from "./AddNewsHyperlinkModal.module.css";
import { RootStoreContext } from "src/stores/RootStore";

const AddNewsHyperlinkModalBase = () => {
    const { t } = useTranslation();
    const { uiStore } = useContext(RootStoreContext);

    const [url, setUrl] = React.useState("");
    const [headline, setHeadline] = React.useState("");
    const [errorEmptyUrl, setErrorEmptyUrl] = React.useState("");

    const isValidUrl = (value: string) => {
        try {
            new URL(value);
            return true;
        } catch {
            return false;
        }
    };

    const onAddNewsHyperlinkModal = useCallback(() => {
        uiStore.setIsAddNewsHyperlinkClicked(false);
    }, [uiStore]);

    const onAddNewsHyperlink = useCallback(() => {
        if (!isValidUrl(url)) {
            setErrorEmptyUrl(t("screens.realestate.news_edit.invalid_url").toString());
            return;
        }
        const createdHyperlink = `<a href="${url}" target="_blank" rel="noopener noreferrer">${
            headline ? headline : url
        }</a>`;

        uiStore.setNewsHyperlink(createdHyperlink);

        uiStore.setIsAddNewsHyperlinkClicked(false);
    }, [url, headline, uiStore, t]);

    const onChangeInput = useCallback(
        (isUrl: boolean) => (e: any) => {
            if (isUrl) {
                setUrl(e.target.value);
            } else {
                setHeadline(e.target.value);
            }
        },
        []
    );

    const subdomain = window.location.host.split(".")[0];

    return (
        <UI.Modal
            show={uiStore.isAddNewsHyperlinkClicked}
            onClose={onAddNewsHyperlinkModal}
            isScrollable={true}
            className={styles.AddNewsHyperlinkModal}
            title={t("screens.realestate.news_edit.add_hyperlink_title").toString()}
        >
            <UI.Input
                label={"URL*"}
                type="text"
                autoComplete="off"
                placeholder={`https://www.${subdomain}.ch/`}
                value={url}
                onChange={onChangeInput(true)}
                autoFocus={true}
                errorMsg={errorEmptyUrl}
            />
            <UI.Input
                label={t("screens.realestate.news_edit.optional_headline").toString()}
                type="text"
                autoComplete="off"
                value={headline}
                onChange={onChangeInput(false)}
            />
            <div className={styles.DivModalButton}>
                <UI.Button
                    className={`${styles.CancelButton} mt-3`}
                    label={t("labels.cancel")}
                    onClick={onAddNewsHyperlinkModal}
                    variant="outline-primary"
                />
                <UI.Button
                    className={`${styles.AddButton} mt-3`}
                    label={t("labels.add")}
                    onClick={onAddNewsHyperlink}
                />
            </div>
        </UI.Modal>
    );
};

export const AddNewsHyperlinkModal = Sentry.withProfiler(observer(AddNewsHyperlinkModalBase));
